import { DOKUMENTE, type Dokumentart } from '../kapitel';
import {
    abschnittVon,
    istBestaetigt,
    kapitelDesDokuments,
    regelnDesKapitels,
    sortiereLuecken,
    sortiereRollen,
} from '../bauordnungLogic';
import { loeseKopfPlatzhalter, tagDeutsch } from '../bausteine/automatik';
import type { KopfFeld } from '../bausteine/platzhalter';
import type { Bauordnung, BauordnungBundle, Regel } from '../types';
import type { Dokument, Knoten, Regelpunkt } from './dokumentbeschreibung';
import type { ProjektMerkmale } from '../../planungsphase/types';
import { ohneGedankenstriche } from '../../../../supabase/functions/_shared/regelwerk';

/**
 * Baut die Beschreibung EINES Dokuments aus EINEM Datenbestand.
 *
 * Eine Funktion fuer beide Ausgaben, nicht zwei: was ein Dokument fuehrt,
 * steht in seiner Kapitelliste (`DOKUMENTE`), nicht in einer zweiten
 * Bau-Funktion. Zwei Funktionen liefen auseinander, sobald jemand nur eine
 * anfasst — und der Unterschied faellt erst im gedruckten Dokument auf.
 *
 * KOPF-PLATZHALTER: Regelungen und Einleitungen tragen `{revision_nr}`,
 * `{geltung_ende}` & Co. bis hierher unaufgeloest (`bausteine/platzhalter.ts`).
 * Erst hier werden sie aus dem Kopfsatz gelesen — aus DEMSELBEN, aus dem
 * auch das Deckblatt seine Zeilen „Stand", „Fassung", „Geltungsdauer" nimmt.
 * Text und Deckblatt koennen so nicht auseinanderlaufen; nach der Freigabe
 * ist das der freigegebene Kopfsatz mit der Fassung aus dem Freigabefeld.
 *
 * GEDANKENSTRICHE: In der Baustellenordnung steht nie einer (Betreiber
 * 03.11.2026). Die festen Texte sind so geschrieben, aber Entwürfe aus dem
 * Bestand tragen noch alte Katalogtexte mit „—", und Freitext kann einen
 * enthalten. Deshalb läuft JEDER Text aus der Datenbank über `druck`, bevor
 * er in die Beschreibung kommt; der Renderer bekommt nur bereinigten Text.
 *
 * Diese Datei importiert NICHTS aus `jspdf`.
 */

/** Ohne Kopfsatz loest jeder Kopf-Platzhalter zu `[Kurz]` auf — nie zu einem rohen `{name}` im Druck. */
const OHNE_KOPF: Pick<Bauordnung, KopfFeld> = {
    erlassender_firma: '', erlassender_name: '', stand: null, version_label: null,
    geltung_von: null, geltung_bis: null, geltung_ort: '',
};

export interface BauKontext {
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    merkmale: ProjektMerkmale;
}

const ROLLEN_LABEL: Record<string, string> = {
    keine: 'keine Weisungsbefugnis',
    begrenzt_dguv1_6: 'begrenzt (§ 6 DGUV Vorschrift 1)',
    bauleitung_lbo: 'Bauleitung nach LBO',
    arbeitgeber: 'Arbeitgeber gegenüber eigenen Beschäftigten',
};

const ERLASSENDER_LABEL: Record<string, string> = {
    bauherr: 'Bauherr',
    gu: 'Generalunternehmer',
    bauleitung: 'Bauleitung',
};

/** Gespeicherter Text → gedruckter Text: ohne Gedankenstrich, Rand ab. */
const druck = (text: string | null | undefined): string => ohneGedankenstriche(String(text ?? '')).trim();

/** Satz unter dem Grundsatz, solange die Fassung nicht freigegeben ist. */
export const ENTWURF_SATZ = 'Diese Fassung ist ein Entwurf. Sie ist nicht verbindlich und nicht zur Weitergabe bestimmt.';

const GRUNDSATZ = 'Dieses Dokument ergänzt den SiGe-Plan, es ersetzt ihn nicht. Es ersetzt weder die '
    + 'Gefährdungsbeurteilung nach § 5 ArbSchG noch die Unterweisung nach § 12 ArbSchG; '
    + 'beide bleiben Aufgabe des jeweiligen Arbeitgebers für seine eigenen Beschäftigten.';

/**
 * Nummer einer Regel im Kapitel: „7.1", „C.2". Bei „Anhang 1" und bei
 * Rahmenkapiteln ohne Ziffer keine Nummer: „Anhang 1.2" liest sich wie eine
 * Fundstelle, nicht wie ein Absatz.
 */
const regelNummer = (kapitelNummer: string, index: number): string | undefined =>
    (/^(\d+|[A-Z]{1,2})$/.test(kapitelNummer) ? `${kapitelNummer}.${index + 1}` : undefined);

export const baueDokument = (
    bundle: BauordnungBundle,
    dokument: Dokumentart,
    kontext: BauKontext,
): Dokument => {
    const def = DOKUMENTE[dokument];
    const o = bundle.ordnung;
    const knoten: Knoten[] = [];
    /** Gespeicherter Text → gedruckter Text: Kopf-Platzhalter aus dem Kopfsatz, ohne Gedankenstrich, Rand ab. */
    const imDruck = (text: string): string => druck(loeseKopfPlatzhalter(text, o ?? OHNE_KOPF));

    const gezogen = kapitelDesDokuments(bundle, dokument, kontext.merkmale);
    const offeneLuecken = sortiereLuecken(bundle.luecken).filter(l => !l.erledigt_am);
    const entwurf = o?.status !== 'freigegeben';

    // ─── Titelseite ──────────────────────────────────────────────────────
    const projektName = druck(kontext.projektName);
    const projektNummer = druck(kontext.projektNummer);
    const projektAdresse = druck(kontext.projektAdresse);
    const standDatum = tagDeutsch(o?.stand) || druck(o?.stand);

    // Der Erlassende ist HERAUSGEBER und steht oben auf der Titelseite. Das
    // verfassende Buero erscheint ausschliesslich klein in der Fusszeile —
    // wer nicht erlaesst, unterschreibt auch nicht. In der Angabenliste steht
    // er deshalb nicht noch einmal.
    const herausgeber = {
        firma: druck(o?.erlassender_firma),
        rolle: o?.erlassender_rolle ? ERLASSENDER_LABEL[o.erlassender_rolle] || '' : '',
    };

    const deckblatt: { merkmal: string; angabe: string }[] = [];
    // Das Bauvorhaben ist die Hauptsache der Angabenliste und steht immer oben.
    deckblatt.push({ merkmal: 'Bauvorhaben', angabe: projektName || 'keine Angabe' });
    if (projektNummer) deckblatt.push({ merkmal: 'Projektnummer', angabe: projektNummer });
    if (projektAdresse) deckblatt.push({ merkmal: 'Baustelle', angabe: projektAdresse });
    if (o?.geltung_ort?.trim()) deckblatt.push({ merkmal: 'Geltungsbereich', angabe: druck(o.geltung_ort) });
    // Datum wie in den Regeltexten (TT.MM.JJJJ, `loeseKopfPlatzhalter`) — ein
    // ISO-Datum auf dem Deckblatt neben „23.08.2026" im Text fiele zuerst auf.
    if (o?.geltung_von || o?.geltung_bis) {
        deckblatt.push({
            merkmal: 'Geltungsdauer',
            angabe: `${tagDeutsch(o?.geltung_von) || druck(o?.geltung_von) || 'offen'} bis ${tagDeutsch(o?.geltung_bis) || druck(o?.geltung_bis) || 'offen'}`,
        });
    }
    if (standDatum) deckblatt.push({ merkmal: 'Stand', angabe: standDatum });
    if (o?.version_label?.trim()) deckblatt.push({ merkmal: 'Fassung', angabe: druck(o.version_label) });
    deckblatt.push({
        merkmal: 'Status',
        angabe: entwurf
            ? 'Entwurf, nicht freigegeben'
            : `freigegeben${tagDeutsch(o?.freigegeben_am) ? ` am ${tagDeutsch(o?.freigegeben_am)}` : ''}`,
    });

    // Der Grundsatz steht in der Vorbemerkung jeder Ausgabe. Ein Entwurf sagt
    // dort in einem schlichten Satz, dass er keiner ist, auf den man sich
    // berufen kann; ein farbiger Kasten auf der Titelseite war das Gegenteil
    // der Gestaltung unserer anderen Dokumente.
    const vorbemerkung = [...(entwurf ? [ENTWURF_SATZ] : []), GRUNDSATZ];

    let bauvorhaben = projektName;
    if (projektName && projektNummer) bauvorhaben = `${projektName} (Projekt-Nr. ${projektNummer})`;
    else if (projektNummer) bauvorhaben = `Projekt-Nr. ${projektNummer}`;

    // ─── Kapitel ─────────────────────────────────────────────────────────
    // Ein Dokument mit Teilen (HSE-Plan) setzt die Teile auf Ebene 1 und ihre
    // Kapitel auf Ebene 2. Die Baustellenordnung hat nur einen Teil und keine
    // Teilüberschrift; ihre Kapitel SIND die oberste Gliederung.
    const mitTeilen = def.teile.length > 1;
    let letzterTeil = '';
    for (const g of gezogen) {
        const kennung = g.definition.id;
        const unterTeil = mitTeilen && g.teil.nummerierung !== 'ohne';
        const ebene = unterTeil ? 2 : 1;
        const titel = druck(g.definition.titel);

        // Teilüberschrift, sobald ein neuer Teil beginnt (HSE-Plan).
        if (g.teil.id !== letzterTeil) {
            letzterTeil = g.teil.id;
            if (unterTeil) {
                knoten.push({ art: 'seitenumbruch' });
                knoten.push({ art: 'ueberschrift', ebene: 1, text: druck(g.teil.titel) });
                if (g.teil.hinweis) knoten.push({ art: 'absatz', text: druck(g.teil.hinweis), betont: true });
            }
        }

        const abschnitt = abschnittVon(bundle.abschnitte, kennung);
        const regeln: Regel[] = regelnDesKapitels(bundle.regeln, kennung).filter(istBestaetigt);
        const lueckenHier = offeneLuecken.filter(x => x.kapitel === kennung);

        // Ein ausdrücklich ausgeschlossenes Kapitel wird NICHT still
        // weggelassen: „geprüft, trifft nicht zu" sagt etwas anderes als ein
        // fehlendes Kapitel.
        if (abschnitt && !abschnitt.gezogen) {
            knoten.push({ art: 'ueberschrift', ebene, nummer: g.nummer, text: titel, kapitel: kennung });
            const bemerkung = druck(abschnitt.bemerkung);
            knoten.push({
                art: 'leerbefund',
                text: bemerkung
                    ? `Geprüft, trifft für dieses Bauvorhaben nicht zu. ${bemerkung}`
                    : 'Geprüft, trifft für dieses Bauvorhaben nicht zu.',
            });
            continue;
        }

        // Das Kapitel „Deckblatt und Geltung" IST die Titelseite. Hat es
        // darüber hinaus nichts Eigenes (Einleitung, Regel, offener Punkt),
        // stünde im Text nur eine Überschrift ohne Inhalt.
        if (kennung === 'deckblatt' && !abschnitt?.einleitung.trim() && !regeln.length && !lueckenHier.length) {
            continue;
        }

        knoten.push({ art: 'ueberschrift', ebene, nummer: g.nummer, text: titel, kapitel: kennung });

        if (abschnitt?.einleitung.trim()) {
            knoten.push({ art: 'absatz', text: imDruck(abschnitt.einleitung) });
        }

        // ─── Die Sonderkapitel, die sich aus dem Bestand selbst füllen ───
        if (kennung === 'offene_punkte') {
            if (!offeneLuecken.length) {
                knoten.push({ art: 'absatz', text: 'Zum Stand dieses Dokuments bestehen keine offenen Punkte.' });
            } else {
                knoten.push({
                    art: 'tabelle',
                    kopf: ['Nr.', 'Offener Punkt', 'Verantwortlich', 'Frist'],
                    spalten: [{ breite: 12 }, { gewicht: 3 }, { gewicht: 1.4 }, { breite: 24 }],
                    zeilen: offeneLuecken.map(l => [
                        String(l.nr),
                        druck(l.punkt),
                        druck(l.verantwortlich) || 'keine Angabe',
                        tagDeutsch(l.frist) || druck(l.frist) || 'offen',
                    ]),
                });
            }
            continue;
        }

        // Die Rollentabelle IST der Inhalt der Organisation; darunter stünde
        // „keine Regelung erfasst" im Widerspruch zur Tabelle darüber.
        let rollenTabelle = false;
        if (kennung === 'organisation') {
            const rollen = sortiereRollen(bundle.rollen);
            rollenTabelle = rollen.length > 0;
            if (!rollen.length) {
                knoten.push({ art: 'leerbefund', text: 'Die Projektorganisation ist noch nicht erfasst.' });
            } else {
                knoten.push({
                    art: 'tabelle',
                    kopf: ['Rolle', 'Name', 'Firma', 'Erreichbarkeit', 'Weisungsbefugnis'],
                    spalten: [{ gewicht: 1.2 }, { gewicht: 1.1 }, { gewicht: 1.2 }, { gewicht: 1.1 }, { gewicht: 1.6 }],
                    zeilen: rollen.map(r => {
                        const umfang = druck(r.befugnis_umfang);
                        return [
                            druck(r.rolle) || druck(r.funktion) || 'keine Angabe',
                            druck(r.name) || 'keine Angabe',
                            druck(r.firma) || 'keine Angabe',
                            druck(r.erreichbarkeit) || 'keine Angabe',
                            (ROLLEN_LABEL[r.weisungsbefugnis] || 'keine Angabe') + (umfang ? `: ${umfang}` : ''),
                        ];
                    }),
                });
            }
        }

        // ─── Die erfassten Regelungen ────────────────────────────────────
        // Jede Regel mit ihrer Nummer und, wo erfasst, mit Verantwortlich und
        // Rhythmus. Der Wortlaut wird nie gekürzt.
        if (regeln.length) {
            knoten.push({
                art: 'regeln',
                punkte: regeln.map((r, i): Regelpunkt => {
                    const nummer = regelNummer(g.nummer, i);
                    const verantwortlich = imDruck(r.verantwortlich);
                    const rhythmus = imDruck(r.rhythmus);
                    return {
                        ...(nummer ? { nummer } : {}),
                        text: imDruck(r.wortlaut),
                        ...(verantwortlich ? { verantwortlich } : {}),
                        ...(rhythmus ? { rhythmus } : {}),
                    };
                }),
            });
        } else if (!rollenTabelle && kennung !== 'deckblatt' && kennung !== 'revisionsverzeichnis' && kennung !== 'kenntnisnahme') {
            // Leer heisst leer — und wird als solches gedruckt. Ein
            // stillschweigend uebersprungenes Pflichtkapitel behauptet, es
            // gebe dazu nichts zu regeln.
            knoten.push({
                art: 'leerbefund',
                text: g.pflicht
                    ? 'Zu diesem Kapitel ist noch keine Regelung erfasst. Vor Baubeginn zu ergänzen.'
                    : 'Zu diesem Kapitel ist keine Regelung erfasst.',
            });
        }

        // Lücken, die genau an diesem Kapitel hängen, stehen an der Textstelle
        // UND weiter unten in der Sammelliste.
        for (const l of lueckenHier) {
            knoten.push({ art: 'luecke', nr: l.nr, text: druck(l.punkt) });
        }

        // ─── Kenntnisnahme und Unterschrift ──────────────────────────────
        if (kennung === 'kenntnisnahme' || kennung === 'unterschrift') {
            knoten.push({
                art: 'absatz',
                text: 'Mit der Unterschrift wird bestätigt, dass die vorstehenden Regelungen gelesen, '
                    + 'verstanden und an die eigenen Beschäftigten sowie an alle Nachunternehmer '
                    + 'weitergegeben werden.',
            });
            knoten.push({
                art: 'unterschriftsfeld',
                zeilen: [
                    {
                        rolle: druck(o?.erlassender_funktion) || 'Erlassender',
                        name: druck(o?.erlassender_name),
                        firma: druck(o?.erlassender_firma),
                    },
                    ...(kennung === 'kenntnisnahme'
                        ? [{ rolle: 'Auftragnehmer (Gegenzeichnung)', name: '', firma: '' }]
                        : []),
                ],
            });
        }
    }

    return {
        titel: def.titel,
        untertitel: def.untertitel,
        herausgeber,
        bauvorhaben,
        standDatum,
        deckblatt,
        vorbemerkung,
        verfasserZeile: druck(o?.verfasser_zeile),
        entwurf,
        knoten,
    };
};

/**
 * Dateiname der Ausgabe.
 *
 * Ohne Schraegstriche und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage, und dort ist ein Schraegstrich ein neuer Ordner.
 * Umlaute bleiben ausdruecklich stehen — sie sind in beiden Ausgabeformaten
 * unkritisch und in einem deutschen Dateinamen erwartbar.
 */
export const bauordnungDateiname = (
    dokument: Dokumentart,
    projektName: string,
    stand?: string | null,
    endung: 'pdf' | 'docx' = 'pdf',
): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = [
        dokument === 'hse' ? 'HSE-Plan' : 'Baustellenordnung',
        sauber(projektName),
        (stand || '').slice(0, 10) || 'ohne-Datum',
    ];
    return `${teile.filter(Boolean).join('_')}.${endung}`;
};
